<script setup lang="ts">
import {
  SelectionType,
  SelfAssessmentStep,
} from "~/classes/models/self-assessment/self-assessment-step.model";

type DuplicateAttributeReviewItem = {
  step: SelfAssessmentStep;
  attribute: string;
  instanceId: string;
  label: string;
};

const props = defineProps({
  quillRange: {
    type: Object as PropType<{
      start: number;
      end: number;
    }>,
    required: true,
  },

  instance: {
    type: Object as PropType<{
      instanceId: string;
      errorMessage?: string;
      range: {
        start: number;
        end: number;
      };
    }>,
  },

  selectionType: {
    type: String as PropType<SelectionType>,
    required: true,
  },
  // Type selected or highlighted
  type: {
    type: String as PropType<"selected" | "highlighted">,
    default: "highlighted",
  },
  quillEditorSelector: {
    type: String as PropType<string>,
    required: true,
  },
  documentSelector: {
    type: String as PropType<string>,
    required: true,
  },
  scrollOffsetY: {
    type: Number as PropType<number>,
    required: true,
  },
  hoverDelayMs: {
    type: Number as PropType<number>,
    default: 150,
  },
  duplicateAttributeItems: {
    type: Array as PropType<DuplicateAttributeReviewItem[]>,
    default: () => [],
  },
});

const emit = defineEmits(["click", "dblclick", "removeDuplicateAttribute"]);

const x = ref<number | undefined>(undefined);
const y = ref(0);
const height = ref(0);
const roundCorders = ref(false);
const width = ref(25);

// Try to inject the quillEditor if it's provided (optional)
const injectedEditor = inject<any>("quillEditor", null);

const widthOffset = ref(0);
const heightOffset = ref(0);

const rects = ref<
  { x: number; y: number; w: number; h: number; text: string }[]
>([]);

const calculateBounds = async () => {
  // await nextTick();
  // wait 500 ms
  await new Promise((resolve) => setTimeout(resolve, 600));

  rects.value = [];

  const quillEditorContainer = document.querySelector(
    props.quillEditorSelector
  );
  if (!quillEditorContainer) return;

  const headerElement = document.querySelector(".header-container");

  let headerHeight = 0;
  if (headerElement) {
    headerHeight = headerElement.getBoundingClientRect().height;
  }

  let shortcutContainerHeight = 0;
  const shortcutContainer = document.querySelector(".shortcut-container");
  if (shortcutContainer) {
    shortcutContainerHeight = shortcutContainer.getBoundingClientRect().height;
  }

  const paddingOffset = 0;

  if (!injectedEditor.value) return;

  const quill = injectedEditor.value.getQuill?.();

  if (!quill) return;

  // if (text.trim().length == 0) {
  //   x.value = undefined;
  //   return;
  // }

  const bounds = quill.getBounds(
    props.quillRange.start,
    props.quillRange.end
  );

  if (!bounds) return;

  let widthOffset = 0;

  if (props.selectionType == SelectionType.token) {
    widthOffset = 0;
  }

  // We want to get the position of the first character of the first word in the selection
  const firstCharacterBoundsForX = quill.getBounds(props.quillRange.start, 1);
  // If the word is tat the start of aline sometime quill gets confused about it's bounds so we use the second character to make sure we get the correct top position
  const firstCharacterBoundsForY = quill.getBounds(
    props.quillRange.start + 1,
    1
  );

  if (!firstCharacterBoundsForX || !firstCharacterBoundsForY) return;

  rects.value = [
    {
      x: firstCharacterBoundsForX.left + widthOffset + paddingOffset,
      y: firstCharacterBoundsForY.top + headerHeight,
      w: bounds.width - widthOffset,
      h: bounds.height,
      text: quill.getText(props.quillRange.start, props.quillRange.end),
    },
  ];
};

watch(
  () => [props.quillRange],
  (newVal) => {
    calculateBounds();
  },
  {
    immediate: true,
    deep: true,
  }
);

onMounted(() => {
  calculateBounds();

});
</script>

<template>
  <div>
    <DocumentSelectionHighlightRect
      v-for="(rect, index) in rects"
      :key="rect.x"
      :rect="rect"
      :type="type"
      :index="index"
      :quillRange="quillRange"
      :selectionType="selectionType"
      :quillEditorSelector="quillEditorSelector"
      :documentSelector="documentSelector"
      :duplicate-attribute-items="duplicateAttributeItems"
      @click="emit('click')"
      @remove-duplicate-attribute="emit('removeDuplicateAttribute', $event)"
    />
  </div>
</template>

<style scoped>
.slow-pulse {
  animation: slow-pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes slow-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
